import React, { useState, useEffect, useRef } from 'react';
import { getSupabase } from '../../lib/supabase-lazy';
import { useAuth } from '../../contexts/AuthContextLazy';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow, isToday, isYesterday, format } from 'date-fns';
import { 
  BellIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  CalendarIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { BellIcon as BellIconSolid } from '@heroicons/react/24/solid';

interface Notification {
  id: string;
  recipient_id: string;
  recipient_type: string;
  thread_id: string;
  notification_type: string;
  title: string;
  message: string;
  action_url: string;
  is_read: boolean;
  created_at: string;
}

export const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<'client' | 'advisor' | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      determineUserType();
    }
  }, [user]);

  useEffect(() => {
    if (userType) {
      fetchNotifications();
      // Set up real-time subscription
      let subscription: any;
      setupRealtimeSubscription().then(sub => {
        subscription = sub;
      });
      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [userType]);

  const determineUserType = async () => {
    if (!user) return;

    try {
      const supabase = await getSupabase();
      
      // Check if user is a client
      const { data: clientUser } = await supabase
        .from('client_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (clientUser) {
        setUserType('client');
        return;
      }

      // Check if user is an advisor
      const { data: platformUser } = await supabase
        .from('platform_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (platformUser) {
        setUserType('advisor');
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error determining user type:', error);
      }
    }
  };

  const fetchNotifications = async () => {
    try {
      const supabase = await getSupabase();
      
      const { data, error } = await supabase
        .from('client_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      
      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.is_read).length);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching notifications:', error);
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = async () => {
    const supabase = await getSupabase();
    
    return supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'client_notifications'
      }, (payload) => {
        // Add new notification to the list
        setNotifications(prev => [payload.new as Notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(payload.new.title, {
            body: payload.new.message,
            icon: '/logo.png'
          });
        }
      })
      .subscribe();
  };

  const markAsRead = async (notificationId: string) => {
    // Optimistically update the UI first
    const wasUnread = notifications.find(n => n.id === notificationId)?.is_read === false;
    
    if (wasUnread) {
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    
    try {
      const supabase = await getSupabase();
      
      const { error } = await supabase
        .from('client_notifications')
        .eq('id', notificationId)
        .update({ is_read: true, read_at: new Date().toISOString() });

      if (error) throw error;
    } catch (error) {
      // Revert the optimistic update on error
      if (wasUnread) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: false } : n)
        );
        setUnreadCount(prev => prev + 1);
      }
      
      if (import.meta.env.DEV) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      const supabase = await getSupabase();
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      
      const { error } = await supabase
        .from('client_notifications')
        .in('id', unreadIds)
        .update({ is_read: true, read_at: new Date().toISOString() });

      if (error) throw error;
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error marking all as read:', error);
      }
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_message':
        return <ChatBubbleLeftIcon className="h-5 w-5" />;
      case 'document_uploaded':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'deadline_reminder':
        return <CalendarIcon className="h-5 w-5" />;
      case 'status_change':
        return <ExclamationCircleIcon className="h-5 w-5" />;
      default:
        return <BellIcon className="h-5 w-5" />;
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Group notifications by date
  const groupNotificationsByDate = (notifications: Notification[]) => {
    const groups: { [key: string]: Notification[] } = {};
    
    notifications.forEach(notification => {
      const date = new Date(notification.created_at);
      let dateKey: string;
      
      if (isToday(date)) {
        dateKey = 'Today';
      } else if (isYesterday(date)) {
        dateKey = 'Yesterday';
      } else {
        dateKey = format(date, 'MMMM d, yyyy');
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(notification);
    });
    
    return groups;
  };

  const filteredNotifications = notifications.filter(
    n => filter === 'all' || !n.is_read
  );
  const groupedNotifications = groupNotificationsByDate(filteredNotifications);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        {unreadCount > 0 ? (
          <BellIconSolid className="h-6 w-6 text-blue-600" />
        ) : (
          <BellIcon className="h-6 w-6" />
        )}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 -mt-1 -mr-1 px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          
          {/* Enhanced Popout Panel */}
          <div 
            className="absolute right-0 mt-2 w-[450px] bg-white portal-content-shadow rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 z-40 transform transition-all duration-200 ease-out origin-top-right"
            style={{
              animation: 'slideDown 0.2s ease-out',
            }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BellIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    <p className="text-sm text-gray-500">
                      {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1 rounded-md hover:bg-blue-50 transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setShowDropdown(false)}
                    className="text-gray-400 hover:text-gray-500 p-1 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading notifications...</p>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BellIcon className="h-10 w-10 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-1">
                    {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {filter === 'unread' 
                      ? 'All caught up! You\'ve read all your notifications.'
                      : 'You\'ll see notifications here when there\'s activity'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Filter Tabs */}
                  <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-200">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      filter === 'all'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter('unread')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      filter === 'unread'
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Unread
                    {unreadCount > 0 && (
                      <span className="ml-1.5 text-xs">
                        ({unreadCount})
                      </span>
                    )}
                  </button>
                </div>
                
                <div>
                  {Object.entries(groupedNotifications).map(([dateKey, dateNotifications]) => (
                    <div key={dateKey}>
                      <div className="px-6 py-2 bg-gray-50 border-y border-gray-200">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{dateKey}</p>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {dateNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => {
                        markAsRead(notification.id);
                        
                        // Guard against navigation if user type is not determined
                        if (!userType || !user) {
                          if (import.meta.env.DEV) {
                            console.error('Cannot navigate: user type not determined or user not authenticated', {
                              userType,
                              userId: user?.id
                            });
                          }
                          return;
                        }
                        
                        // Handle notification navigation based on user context
                        let targetUrl = notification.action_url;
                        
                        if (import.meta.env.DEV) {
                          console.log('Notification navigation:', {
                            userType,
                            originalUrl: notification.action_url,
                            userId: user?.id
                          });
                        }
                        
                        // For advisor users, ensure they stay in advisor portal
                        if (userType === 'advisor') {
                          // If the URL is already an advisor portal URL, use it as is
                          if (targetUrl.startsWith('/portal/')) {
                            if (import.meta.env.DEV) {
                              console.log('Advisor navigating to advisor portal URL:', targetUrl);
                            }
                            // Close dropdown first, then navigate
                            setShowDropdown(false);
                            // Small delay to ensure dropdown closes before navigation
                            setTimeout(() => {
                              navigate(targetUrl, { replace: false });
                            }, 100);
                          }
                          // If it's a client portal URL, convert to advisor portal equivalent
                          else if (targetUrl.startsWith('/client-portal/')) {
                            // Extract any client ID from the URL
                            const clientIdMatch = targetUrl.match(/\/client-portal\/.*?\/([a-f0-9-]+)/);
                            setShowDropdown(false);
                            setTimeout(() => {
                              if (clientIdMatch) {
                                // Navigate to the client detail page in advisor portal
                                navigate(`/portal/clients/${clientIdMatch[1]}`);
                              } else {
                                // Default to advisor dashboard
                                navigate('/portal/dashboard');
                              }
                            }, 100);
                          }
                          else {
                            // For any other URL, navigate as is
                            setShowDropdown(false);
                            setTimeout(() => {
                              navigate(targetUrl);
                            }, 100);
                          }
                        }
                        // For client users, ensure they stay in client portal
                        else if (userType === 'client') {
                          // If the URL is already a client portal URL, use it as is
                          if (targetUrl.startsWith('/client-portal/')) {
                            setShowDropdown(false);
                            setTimeout(() => {
                              navigate(targetUrl);
                            }, 100);
                          }
                          // If it's an advisor portal URL, redirect to client dashboard
                          else if (targetUrl.startsWith('/portal/')) {
                            // Client users should not access advisor portal
                            // Redirect to their dashboard instead
                            setShowDropdown(false);
                            setTimeout(() => {
                              navigate('/client-portal/dashboard');
                            }, 100);
                          }
                          else {
                            // For any other URL, navigate as is
                            setShowDropdown(false);
                            setTimeout(() => {
                              navigate(targetUrl);
                            }, 100);
                          }
                        }
                        else {
                          // If user type is not determined, close dropdown but don't navigate
                          setShowDropdown(false);
                          if (import.meta.env.DEV) {
                            console.error('User type not determined, cannot navigate');
                          }
                        }
                      }}
                      className={`block px-6 py-4 hover:bg-gray-50 transition-all duration-150 cursor-pointer ${
                        !notification.is_read ? 'bg-blue-50 border-l-4 border-blue-400' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 rounded-lg flex-shrink-0 ${
                          !notification.is_read ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {getNotificationIcon(notification.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" />
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        {!notification.is_read && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          </div>
                        )}
                      </div>
                    </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <Link
                  to="/portal/notifications"
                  onClick={() => setShowDropdown(false)}
                  className="flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 px-4 py-2 rounded-md hover:bg-blue-50 transition-colors"
                >
                  <span>View all notifications</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};